// Network state model. Every lab owns one plain-JSON network object so that it
// can be persisted to localStorage, deep-cloned for "probe" checks, and later
// replaced by state read from a real lab controller (Phase C).
//
//   net = { devices: { [id]: Device }, links: [ Link ] }
//
// Device kinds: 'pc' | 'server' (Windows endpoints), 'switch', 'router'.

let macSeq = 0x10;
const macFor = (oui, n) => oui + n.toString(16).padStart(6, '0');

function iface(name, extra = {}) {
  return {
    name, shutdown: false, description: '', mac: '', ip: null, ipv6: [], ipv6Enabled: false,
    mode: 'access', accessVlan: 1, speed: 'auto', duplex: 'auto',
    nat: null, counters: { inPkts: 0, outPkts: 0, crc: 0, inErrors: 0 }, ...extra,
  };
}

/** Windows endpoint. `ip` may be omitted for DHCP clients. */
export function pc(id, o = {}) {
  return {
    id, kind: o.kind || 'pc', hostname: o.hostname || id, os: o.os || 'Windows 11',
    nic: {
      name: 'FastEthernet0', short: 'Fa0', alias: 'Ethernet', adminUp: true,
      mac: o.mac || macFor('0050796', macSeq++).slice(0, 12),
    },
    ipcfg: {
      dhcp: !!o.dhcp, ip: o.ip || null, mask: o.mask || null, gw: o.gw || null,
      dns: o.dns || [], ipv6: [],
    },
    lease: null,      // DHCP lease { ip, mask, gw, dns, server }
    apipa: null,      // 169.254.x.x when DHCP fails
    arp: {},          // ip -> mac
    services: o.services || [],   // [{ proto:'tcp', port:80, name:'http' }]
    dnsRecords: o.dnsRecords || null,  // { 'www.example.com': '1.2.3.4' } when a DNS server
    http: o.http || null,           // { title, body } served on 80/443
    ttl: o.ttl || 128,
    connections: [],   // recent TCP sessions for netstat
    user: o.user || 'student',
  };
}
export const server = (id, o = {}) => pc(id, { kind: 'server', os: 'Windows Server 2022', ...o });

export function sw(id, o = {}) {
  const interfaces = {};
  const fa = o.fastPorts ?? 24;
  for (let i = 1; i <= fa; i++) {
    interfaces[`FastEthernet0/${i}`] = iface(`FastEthernet0/${i}`, { mac: macFor('000a41', 0x100 * (o.seq || 1) + i) });
  }
  for (let i = 1; i <= (o.gigPorts ?? 2); i++) {
    interfaces[`GigabitEthernet0/${i}`] = iface(`GigabitEthernet0/${i}`, { mac: macFor('000a41', 0x100 * (o.seq || 1) + 0x40 + i) });
  }
  return {
    id, kind: 'switch', hostname: o.hostname || id, model: o.l3 ? 'WS-C3560-24PS' : 'WS-C2960-24TT-L',
    baseMac: macFor('000a41', 0x100 * (o.seq || 1)),
    interfaces, vlans: { 1: { name: 'default' }, ...(o.vlans || {}) },
    macTable: [],        // [{ vlan, mac, port }]
    domainLookup: true, startup: null, logs: [],
    // Domain 02 additions (all optional for Domain 01 switches):
    l3: !!o.l3,              // multilayer switch (Catalyst 3560) — supports `ip routing`
    ipRouting: false,
    defaultGateway: null,    // `ip default-gateway` for a Layer 2 switch's management SVI
    routes: [], arp: {},
    stp: { mode: 'pvst', priority: {} },
  };
}

export function router(id, o = {}) {
  const interfaces = {};
  (o.ports || ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2']).forEach((n, i) => {
    // Routers ship with interfaces administratively down.
    interfaces[n] = iface(n, { mode: 'routed', shutdown: true, mac: macFor('00014' + (o.seq || 2), 0x9a100 + i) });
  });
  return {
    id, kind: 'router', hostname: o.hostname || id, model: 'CISCO2911/K9',
    interfaces, routes: [], acls: {}, nat: { rules: [], translations: [] },
    dhcp: { pools: {}, excluded: [], bindings: [] },
    ipv6Routing: false, arp: {}, domainLookup: true, startup: null,
    managed: o.managed !== false,  // false = learner has no console access (ISP)
    ttl: 255,
  };
}

export function link(a, aIf, b, bIf, cable = {}) {
  return {
    id: `${a}:${aIf}--${b}:${bIf}`,
    a: { dev: a, ifc: aIf }, b: { dev: b, ifc: bIf },
    cable: { type: 'Cat6 UTP', connector: 'RJ45', length: 3, seated: true, damaged: false, compatible: true, ...cable },
  };
}

export const network = (devices, links) => ({
  devices: Object.fromEntries(devices.map((d) => [d.id, d])),
  links,
});

/** Convenience setters used by lab definitions to build baseline state. */
export function setIf(dev, name, patch) {
  Object.assign(dev.interfaces[name], patch);
  return dev;
}
export const clone = (o) => JSON.parse(JSON.stringify(o));
